import { useEffect, useRef, useState } from 'react';
import { HOME } from '@/data/home';
import { Reveal } from '@components/motion';
import FigmaFrame from './FigmaFrame';
import { SOCIAL_URLS } from '@/data/navigation';
import { postsFor } from '@/data/social';
import { SocialFeed } from './SocialEmbeds';
import { FaXTwitter, FaFacebook, FaInstagram, FaYoutube, FaLinkedin } from 'react-icons/fa6';

/* ------------------------------------------------------------------ */
/* DESKTOP — exact Figma transcription                                 */
/* ------------------------------------------------------------------ */

const COL_W = 502;
const COL_TOP = 490;
const COL_H = 608;
const COL_LEFTS = [159, 700, 1241];

/**
 * A labelled feed column: light panel, vertically scrollable embeds, and a
 * scrollbar whose thumb tracks the real scroll position.
 */
function FeedColumn({
    label,
    left,
    delay,
    children,
}: {
    label: string;
    left: number;
    delay: number;
    children: React.ReactNode;
}) {
    const viewportRef = useRef<HTMLDivElement>(null);
    const [thumb, setThumb] = useState({ height: 40, offset: 0 });

    const onScroll = () => {
        const el = viewportRef.current;
        if (!el) return;
        const track = el.clientHeight;
        // Before the embeds have laid out, both measurements are 0 — dividing them
        // yields NaN and React rejects the resulting height. Hold the default until
        // the column has a real size.
        if (!track || !el.scrollHeight) return;
        const ratio = Math.min(1, track / el.scrollHeight);
        const height = Math.max(48, track * ratio);
        const max = el.scrollHeight - track;
        const offset = max > 0 ? (el.scrollTop / max) * (track - height) : 0;
        setThumb({ height, offset });
    };

    useEffect(onScroll, []);

    return (
        <div className="absolute" style={{ left, top: COL_TOP - 62 }}>
            <Reveal as="div" once variant="fade-up" delay={delay}>
                {/* Column label */}
                <p className="mb-[18px] text-[30px] font-bold leading-[40px] text-[#1b1b1b]">{label}</p>

                <div className="relative" style={{ width: COL_W, height: COL_H }}>
                    {/* Panel surface */}
                    <div className="absolute inset-0 bg-[#f7f7f7]" />

                    {/* Scrollable embeds */}
                    <div
                        ref={viewportRef}
                        onScroll={onScroll}
                        className="scrollbar-hide absolute left-[22px] top-[32px] flex flex-col gap-[24px] overflow-y-auto"
                        style={{ width: COL_W - 66, height: COL_H - 64 }}
                    >
                        {children}
                    </div>

                    {/* Scrollbar (thumb follows the scroll position) */}
                    <div
                        className="absolute right-[14px] w-[7px] rounded-[23px] bg-[#f0f0f0]"
                        style={{ top: 32, height: COL_H - 64 }}
                    >
                        <div
                            className="w-full rounded-[23px] bg-[#1b1b1b] transition-[height,transform] duration-150"
                            style={{ height: thumb.height, transform: `translateY(${thumb.offset}px)` }}
                        />
                    </div>
                </div>
            </Reveal>
        </div>
    );
}

function SocialMediaDesktop() {
    return (
        <FigmaFrame designWidth={1920} designHeight={1160} className="w-full">
            <div className="relative h-[1160px] w-[1920px] bg-white text-[#1b1b1b]">
                {/* heading + blurb */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    className="absolute left-[159px] top-[67px] flex flex-col items-start gap-[27px]"
                >
                    <h2 className="w-[606px] text-[96px] font-extrabold leading-[103px] text-[#1b1b1b]">
                        <span className="font-normal text-[#6c6c6c]">{HOME.social.titleLead}</span>
                        <br />
                        <span>{HOME.social.titleBold}</span>
                    </h2>
                    <p className="h-[61px] w-[606px] text-[14px] font-normal leading-[24px] text-[#333]">
                        {HOME.social.blurb}
                    </p>
                </Reveal>

                {/* social icon row (top-right) */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={150}
                    className="absolute left-[1432px] top-[302px] flex items-center gap-[64px]"
                >
                    {ACCOUNTS.map(({ Icon, label, network }) =>
                        SOCIAL_URLS[network] ? (
                            <a
                                key={label}
                                href={SOCIAL_URLS[network]}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={label}
                            >
                                <Icon className="size-[38px] text-[#1b1b1b]" />
                            </a>
                        ) : null,
                    )}
                </Reveal>

                {/* Feed columns */}
                <FeedColumn label="X" left={COL_LEFTS[0]} delay={0}>
                    <SocialFeed network="x" posts={postsFor('x')} />
                </FeedColumn>

                <FeedColumn label="Instagram" left={COL_LEFTS[1]} delay={150}>
                    <SocialFeed network="instagram" posts={postsFor('instagram')} />
                </FeedColumn>

                <FeedColumn label="LinkedIn" left={COL_LEFTS[2]} delay={300}>
                    <SocialFeed network="linkedin" posts={postsFor('linkedin')} />
                </FeedColumn>
            </div>
        </FigmaFrame>
    );
}

/* ------------------------------------------------------------------ */
/* MOBILE — clean single-column stack                                  */
/* ------------------------------------------------------------------ */

type SocialTab = 'x' | 'instagram' | 'linkedin';

function SocialMediaMobile() {
    const [tab, setTab] = useState<SocialTab>('x');

    return (
        <section className="bg-white px-5 pt-[25px] text-[#1b1b1b]">
            {/* heading + blurb + social rail */}
            <div className="relative">
                <div className="flex max-w-[301px] flex-col gap-[13px]">
                    <h2 className="text-[40px] font-extrabold leading-[50px] text-[#1b1b1b]">
                        <span className="font-normal text-[#6c6c6c]">{HOME.social.titleLead}</span>
                        <br />
                        <span>{HOME.social.titleBold}</span>
                    </h2>
                    <p className="max-w-[273px] text-[14px] font-normal leading-[24px] text-[#333]">
                        {HOME.social.blurb}
                    </p>
                </div>

                <div className="absolute right-0 top-[66px] flex flex-col gap-[27px]">
                    <FaXTwitter className="size-[19px] text-[#1b1b1b]" />
                    <FaFacebook className="size-[19px] text-[#1b1b1b]" />
                    <FaInstagram className="size-[19px] text-[#1b1b1b]" />
                    <FaYoutube className="size-[19px] text-[#1b1b1b]" />
                </div>
            </div>

            {/* tabs */}
            <div className="mt-[34px] flex items-center bg-white">
                <button
                    type="button"
                    onClick={() => setTab('x')}
                    className={`flex h-[34px] items-center gap-[9px] px-[8px] py-[4px] ${
                        tab === 'x' ? 'border-b border-[#6c6c6c]' : ''
                    }`}
                >
                    <FaXTwitter className="size-[12px] text-black" />
                    <span className={`text-[14px] ${tab === 'x' ? 'text-[#333]' : 'text-[#b6b6b6]'}`}>X</span>
                </button>
                <button
                    type="button"
                    onClick={() => setTab('instagram')}
                    className={`ml-[38px] flex h-[34px] items-center gap-[9px] rounded-[4px] px-[8px] py-[4px] ${
                        tab === 'instagram' ? 'border-b border-[#6c6c6c]' : ''
                    }`}
                >
                    <FaInstagram className={`size-[18px] ${tab === 'instagram' ? 'text-black' : 'text-[#b6b6b6]'}`} />
                    <span className={`text-[14px] ${tab === 'instagram' ? 'text-[#333]' : 'text-[#b6b6b6]'}`}>
                        Instagram
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => setTab('linkedin')}
                    className={`ml-[38px] flex h-[34px] items-center gap-[9px] rounded-[4px] px-[8px] py-[4px] ${
                        tab === 'linkedin' ? 'border-b border-[#6c6c6c]' : ''
                    }`}
                >
                    <FaLinkedin className={`size-[18px] ${tab === 'linkedin' ? 'text-[#0a66c2]' : 'text-[#b6b6b6]'}`} />
                    <span className={`text-[14px] ${tab === 'linkedin' ? 'text-[#333]' : 'text-[#b6b6b6]'}`}>
                        LinkedIn
                    </span>
                </button>
            </div>

            {/* feed card */}
            <div className="relative mt-[33px]">
                {/* corner badge */}
                <div className="absolute left-[17px] top-[21px] z-10 flex h-[51.52px] w-[56px] items-center justify-center bg-white shadow-[-33px_31px_70.6px_-5px_rgba(0,0,0,0.31)]">
                    {tab === 'x' && <FaXTwitter className="size-[19px] text-black" />}
                    {tab === 'instagram' && <FaInstagram className="size-[22px] text-[#1b1b1b]" />}
                    {tab === 'linkedin' && <FaLinkedin className="size-[22px] text-[#0a66c2]" />}
                </div>

                {/* scrollbar */}
                <div className="pointer-events-none absolute right-0 top-[94px] z-10 h-[613px] w-[7px]">
                    <div className="absolute inset-0 rounded-[23px] bg-[#f0f0f0]" />
                    <div className="absolute left-0 top-0 h-[223px] w-[7px] rounded-[23px] bg-[#1b1b1b]" />
                </div>

                {/* feed surface */}
                <div className="h-[490px] overflow-y-auto bg-[rgba(244,244,244,0.64)] pb-[40px] pr-[14px] pt-[57px]">
                    <div className="mx-auto flex w-[308px] flex-col gap-[24px]">
                        <SocialFeed key={tab} network={tab} posts={postsFor(tab)} />
                    </div>
                </div>
            </div>
        </section>
    );
}

/** The accounts this band links to, in the order the design shows them. */
const ACCOUNTS = [
    { Icon: FaXTwitter, label: 'X', network: 'x' },
    { Icon: FaFacebook, label: 'Facebook', network: 'facebook' },
    { Icon: FaInstagram, label: 'Instagram', network: 'instagram' },
    { Icon: FaYoutube, label: 'YouTube', network: 'youtube' },
];

export default function SocialMedia() {
    return (
        <>
            <div className="hidden lg:block">
                <SocialMediaDesktop />
            </div>
            <div className="lg:hidden">
                <SocialMediaMobile />
            </div>
        </>
    );
}
